import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAuthStore } from './auth'

/** Typy událostí, které posílá backend (viz App\Realtime\Event). */
export type RealtimeEvent =
  | 'notification.created'
  | 'notification.read'
  | 'user.created'
  | 'user.updated'
  | 'user.deleted'
  | 'stats.changed'
  | 'session.revoked'

export interface RealtimeMessage {
  event: RealtimeEvent
  data: Record<string, unknown>
}

type Handler = (message: RealtimeMessage) => void

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed'

const HUB_URL = '/.well-known/mercure'

/**
 * Odběr událostí z Mercure hubu.
 *
 * Zprávy nesou jen typ a identifikátory; obrazovky si podle nich znovu
 * načtou data přes API, kde platí běžná oprávnění. Kdyby se stav bral přímo
 * ze zprávy, obcházela by se autorizace na čtení.
 */
export const useRealtimeStore = defineStore('realtime', () => {
  const state = ref<ConnectionState>('idle')
  const lastEventAt = ref<Date | null>(null)

  const handlers = new Map<RealtimeEvent, Set<Handler>>()
  let source: EventSource | null = null

  const isConnected = computed(() => state.value === 'open')

  function topicsFor(): string[] {
    const auth = useAuthStore()
    const user = auth.user
    if (!user) return []

    // Musí odpovídat App\Realtime\Topic::subscribableBy — hub jiné kanály
    // stejně nepustí, tohle jen šetří zbytečný pokus.
    const topics = [`/users/${user.id}`]
    if (auth.isAdmin) {
      topics.push('/admin/users', '/admin/stats')
    }

    return topics
  }

  function dispatch(raw: string): void {
    let message: RealtimeMessage
    try {
      message = JSON.parse(raw) as RealtimeMessage
    } catch {
      // Poškozená zpráva nesmí shodit spojení.
      return
    }

    if (!message?.event) return

    lastEventAt.value = new Date()
    for (const handler of handlers.get(message.event) ?? []) {
      try {
        handler(message)
      } catch (error) {
        console.error('Obsluha události selhala:', message.event, error)
      }
    }
  }

  function connect(): void {
    if (source) return

    const topics = topicsFor()
    if (topics.length === 0) return

    const url = new URL(HUB_URL, window.location.origin)
    for (const topic of topics) {
      url.searchParams.append('topic', topic)
    }

    state.value = 'connecting'
    // Autorizace jde v httpOnly cookie, kterou vydal login — proto stačí
    // withCredentials a token se nikdy neobjeví v JS.
    source = new EventSource(url, { withCredentials: true })

    source.onopen = () => {
      state.value = 'open'
    }

    source.onmessage = (event: MessageEvent<string>) => dispatch(event.data)

    source.onerror = () => {
      // EventSource se připojuje sám; jen dáme najevo, že spojení kleslo.
      state.value = source?.readyState === EventSource.CLOSED ? 'closed' : 'connecting'
    }
  }

  function disconnect(): void {
    source?.close()
    source = null
    state.value = 'idle'
  }

  /**
   * Zaregistruje obsluhu a vrátí funkci pro odhlášení — komponenty ji volají
   * v onUnmounted, aby po sobě uklidily.
   */
  function on(event: RealtimeEvent, handler: Handler): () => void {
    const set = handlers.get(event) ?? new Set<Handler>()
    set.add(handler)
    handlers.set(event, set)

    return () => {
      set.delete(handler)
    }
  }

  /** Připojí se k odběru více událostí naráz. */
  function onAny(events: RealtimeEvent[], handler: Handler): () => void {
    const offs = events.map((event) => on(event, handler))

    return () => offs.forEach((off) => off())
  }

  function reset(): void {
    disconnect()
    handlers.clear()
    lastEventAt.value = null
  }

  return { state, isConnected, lastEventAt, connect, disconnect, on, onAny, reset }
})
